import React, {FC, useMemo, useState} from "react";
import GatewayService from "../../service/GatewayService";

interface Trade {
    symbol: string;
    trade_date: string;
    trade_time: string;
    trade_type: 'buy' | 'sell';
    price: number;
    quantity: number;
    intrinsic_value?: number;
    pnl?: number;
    cumulative_P_L?: number;
}

const TradeAnalyzerComponent: FC = () => {
    const [csv, setCsv] = useState('');
    const [trades, setTrades] = useState<Trade[]>([]);
    const [showCsvInput, setShowCsvInput] = useState(true);
    const [symbolFilter, setSymbolFilter] = useState('');

    const handleParse = () => {
        const lines = csv.trim().split('\n');
        if (lines.length < 2) return;

        const header = lines[0].split(',').map(h => h.trim());
        const symbolIdx = header.indexOf('symbol');
        const dateIdx = header.indexOf('trade_date');
        const typeIdx = header.indexOf('trade_type');
        const qtyIdx = header.indexOf('quantity');
        const priceIdx = header.indexOf('price');
        const timeIdx = header.indexOf('order_execution_time');

        if ([symbolIdx, dateIdx, typeIdx, qtyIdx, priceIdx, timeIdx].includes(-1)) {
            alert('CSV header is missing required columns. Required: symbol, trade_date, trade_type, quantity, price, order_execution_time');
            return;
        }

        const parsedTrades: Trade[] = lines.slice(1).map(line => {
            const values = line.split(',');
            return {
                symbol: values[symbolIdx],
                trade_date: values[dateIdx],
                trade_time: new Date(values[timeIdx]).toLocaleTimeString(),
                trade_type: values[typeIdx] as 'buy' | 'sell',
                quantity: parseFloat(values[qtyIdx]),
                price: parseFloat(values[priceIdx]),
            };
        });
        setTrades(parsedTrades);
    };

    const fetchIntrinsicValues = async () => {
        const symbols = Array.from(new Set(trades.map(t => t.symbol)));
        if (symbols.length === 0) return;

        try {
            const intrinsics = await GatewayService.getIntrinsicValues(symbols);
            let cumulativePnl = 0;
            const updatedTrades = trades.map(trade => {
                const intrinsic_value = intrinsics[trade.symbol] || 0;
                let pnl = 0;
                if (trade.trade_type === 'buy') {
                    pnl = (-trade.price + intrinsic_value) * trade.quantity;
                } else { // sell
                    pnl = (trade.price - intrinsic_value) * trade.quantity;
                }
                cumulativePnl += pnl;
                return {...trade, intrinsic_value, pnl, cumulative_P_L: cumulativePnl};
            });
            setTrades(updatedTrades);
        } catch (error) {
            console.error("Failed to fetch intrinsic values", error);
        }
    };

    const selectedTrades = useMemo(() => {
        if (!symbolFilter) return [];
        return trades.filter(t => t.symbol.toLowerCase().includes(symbolFilter.toLowerCase()));
    }, [trades, symbolFilter]);

    const filteredTrades = useMemo(() => {
        if (!symbolFilter) return trades;
        return trades.filter(t => t.symbol.toLowerCase().includes(symbolFilter.toLowerCase()));
    }, [trades, symbolFilter]);


    const deleteSelected = () => {
        if (!symbolFilter) return;
        setTrades(trades.filter(t => !selectedTrades.includes(t)));
    };

    const deleteUnselected = () => {
        if (!symbolFilter) return;
        setTrades(selectedTrades);
    };

    const summary = useMemo(() => {
        return trades.reduce((acc, trade) => {
            const priceMultiplier = trade.trade_type === 'sell' ? 1 : -1;
            const valueMultiplier = trade.trade_type === 'buy' ? 1 : -1;

            acc.moneyInvested += trade.price * trade.quantity * priceMultiplier;
            if (trade.intrinsic_value !== undefined) {
                acc.currentValue += trade.intrinsic_value * trade.quantity * valueMultiplier;
            }
            return acc;
        }, {moneyInvested: 0, currentValue: 0});
    }, [trades]);

    return (
        <div>
            <h2>Trade Analyzer</h2>
            <button onClick={() => setShowCsvInput(!showCsvInput)}>
                {showCsvInput ? 'Hide' : 'Show'} CSV Input
            </button>
            {showCsvInput && (
                <div>
                    <textarea
                        rows={10}
                        cols={100}
                        value={csv}
                        onChange={e => setCsv(e.target.value)}
                        placeholder="Paste CSV data here"
                    ></textarea>
                    <br />
                    <button onClick={handleParse}>Parse CSV</button>
                </div>
            )}
            <hr />
            <div>
                <input
                    type="text"
                    placeholder="Filter by symbol"
                    value={symbolFilter}
                    onChange={e => setSymbolFilter(e.target.value)}
                />
                <button onClick={deleteSelected}>Delete Selected</button>
                <button onClick={deleteUnselected}>Delete Un-selected</button>
                <button onClick={fetchIntrinsicValues}>Fetch Intrinsic Value</button>
            </div>

            <h3>Summary</h3>
            <div>
                <span>Total Money Invested: {summary.moneyInvested.toFixed(2)}</span>
                <span style={{marginLeft: '20px'}}>Current Value: {summary.currentValue.toFixed(2)}</span>
            </div>

            <table className="full">
                <thead>
                <tr>
                    <th>symbol</th>
                    <th>trade_date</th>
                    <th>trade_time</th>
                    <th>trade_type</th>
                    <th>price</th>
                    <th>qty</th>
                    <th>intrinsic_value</th>
                    <th>p&l</th>
                    <th>commulative_P&L</th>
                </tr>
                </thead>
                <tbody>
                {filteredTrades.map((trade, index) => (
                    <tr key={index}>
                        <td>{trade.symbol}</td>
                        <td>{trade.trade_date}</td>
                        <td>{trade.trade_time}</td>
                        <td>{trade.trade_type}</td>
                        <td>{trade.price.toFixed(2)}</td>
                        <td>{trade.quantity}</td>
                        <td>{trade.intrinsic_value?.toFixed(2)}</td>
                        <td>{trade.pnl?.toFixed(2)}</td>
                        <td>{trade.cumulative_P_L?.toFixed(2)}</td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
};

export default TradeAnalyzerComponent;
